import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Building2, Copy, ImageUp, Loader2, QrCode, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, fmtDate, rupiah } from "@/lib/api";
import { useApi, Skeleton, StatusBadge, btnPrimary, inputCls } from "@/components/portal/ui";

const PRESETS = [50000, 100000, 250000, 500000, 1000000];

function loadSnap(clientKey, production) {
  return new Promise((resolve, reject) => {
    if (window.snap) return resolve(window.snap);
    const s = document.createElement("script");
    s.src = production ? "https://app.midtrans.com/snap/snap.js" : "https://app.sandbox.midtrans.com/snap/snap.js";
    s.setAttribute("data-client-key", clientKey);
    s.onload = () => resolve(window.snap);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

function Chip({ active, children, ...rest }) {
  return <button type="button" className={`rounded-xl px-3 py-3 text-sm transition active:scale-95 border ${active ? "bg-blue-600 border-blue-600 text-white font-bold" : "bg-white border-slate-200 text-slate-600 font-semibold hover:bg-slate-50"}`} {...rest}>{children}</button>;
}

function History({ items, onRefresh }) {
  return (
    <div className="space-y-3" data-testid="offering-history">
      <p className="px-1 text-sm font-bold text-slate-900">Riwayat</p>
      {items.length === 0 && <p className="text-sm text-slate-500 px-1">Belum ada persembahan.</p>}
      {items.map((o) => (
        <div key={o.id} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center gap-4" data-testid={`offering-${o.id}`}>
          <span className="w-11 h-11 rounded-xl bg-blue-100 text-blue-700 grid place-items-center">{o.method === "online" ? <QrCode className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}</span>
          <div className="flex-1 min-w-0"><p className="font-bold text-slate-900 text-sm">{rupiah(o.amount)}</p><p className="text-xs text-slate-500 truncate">{o.category}{o.provider ? ` · ${o.provider}` : ""} · {fmtDate(o.created_at, { day: "numeric", month: "short" })}</p></div>
          <div className="flex items-center gap-2">
            <StatusBadge status={o.status} />
            {o.method === "online" && o.status === "pending" && <button onClick={() => onRefresh(o)} data-testid={`offering-refresh-${o.id}`} className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center"><RefreshCw className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Persembahan() {
  const { data: cfg } = useApi("/portal/payment-config");
  const { data: history, reload } = useApi("/portal/offerings");
  const [form, setForm] = useState({ category: "Persepuluhan", amount: 100000, method: "manual", note: "" });
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const gateways = cfg?.gateways || [];
  const autoChecked = useRef(false);

  useEffect(() => {
    if (!history || autoChecked.current) return;
    autoChecked.current = true;
    const pending = history.filter((o) => o.method === "online" && o.status === "pending");
    if (pending.length) Promise.allSettled(pending.map((o) => api.post(`/portal/offerings/${o.id}/refresh`))).then(reload);
  }, [history, reload]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setProof({ path: data.path, name: file.name, preview: URL.createObjectURL(file) });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const refresh = async (o) => {
    try {
      const { data } = await api.post(`/portal/offerings/${o.id}/refresh`);
      toast(`Status: ${data.status}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submit = async () => {
    if (form.method === "manual" && !proof) return toast.error("Unggah bukti transfer terlebih dahulu");
    setBusy(true);
    try {
      const online = form.method !== "manual";
      const payload = { category: form.category, note: form.note, amount: Number(form.amount), proof_path: online ? null : proof?.path,
        method: online ? "online" : "manual", gateway: online ? form.method : null, return_url: `${window.location.origin}/portal/persembahan` };
      const { data } = await api.post("/portal/offerings", payload);
      if (data.invoice_url) {
        toast("Mengalihkan ke halaman pembayaran Xendit…");
        window.location.assign(data.invoice_url);
        return;
      }
      if (data.snap_token) {
        const snap = await loadSnap(cfg.midtrans_client_key, cfg.midtrans_production);
        snap.pay(data.snap_token, { onSuccess: () => refresh(data), onPending: () => refresh(data), onClose: () => refresh(data), onError: () => toast.error("Pembayaran gagal") });
      } else {
        toast.success("Terima kasih! Persembahan menunggu verifikasi admin.");
      }
      setProof(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return <div className="p-6 pt-12"><Skeleton className="h-96" /></div>;

  return (
    <div data-testid="portal-persembahan">
      <div className="relative overflow-hidden rounded-b-[2.5rem] bg-blue-600 text-white px-6 pt-10 pb-16">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10" />
        <Link to="/portal" data-testid="offering-back" className="relative w-10 h-10 rounded-full bg-white/15 grid place-items-center"><ArrowLeft className="w-5 h-5" /></Link>
        <p className="relative mt-6 text-xs font-bold uppercase tracking-[0.2em] text-blue-100">Memberi dengan sukacita</p>
        <h1 className="relative text-3xl font-extrabold tracking-tight mt-1">Persembahan</h1>
        <p className="relative mt-4 text-4xl font-black tabular-nums" data-testid="offering-amount-display">{rupiah(form.amount)}</p>
      </div>

      <div className="px-5 -mt-8 relative z-10 space-y-5">
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-5">
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Kategori</p>
            <div className="flex flex-wrap gap-2">{cfg.categories.map((c) => <Chip key={c} active={form.category === c} onClick={() => setForm({ ...form, category: c })} data-testid={`offering-cat-${c.replace(/\s/g, "-")}`}>{c}</Chip>)}</div>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Nominal</p>
            <div className="grid grid-cols-3 gap-2">{PRESETS.map((p) => <Chip key={p} active={Number(form.amount) === p} onClick={() => setForm({ ...form, amount: p })} data-testid={`offering-amount-${p}`}>{p >= 1000000 ? "1 jt" : `${p / 1000}rb`}</Chip>)}</div>
            <input data-testid="offering-amount-input" type="number" min={10000} step={1000} className={`${inputCls} mt-3`} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Nominal lain" />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Metode</p>
            <div className="grid grid-cols-2 gap-2">
              <Chip active={form.method === "manual"} onClick={() => setForm({ ...form, method: "manual" })} data-testid="offering-method-manual">Transfer Manual</Chip>
              {gateways.map((g) => (
                <Chip key={g} active={form.method === g} onClick={() => setForm({ ...form, method: g })} data-testid={`offering-method-${g}`}>{g === "midtrans" ? "Midtrans · QRIS/VA" : "Xendit · QRIS/VA"}</Chip>
              ))}
              {gateways.length === 0 && <Chip disabled data-testid="offering-method-online">Online (nonaktif)</Chip>}
            </div>
          </div>

          {form.method === "manual" && (
            <div className="space-y-3" data-testid="offering-manual-panel">
              <div className="rounded-2xl bg-slate-900 text-white p-5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">Rekening {cfg.bank_name}</p>
                <div className="mt-1 flex items-center justify-between"><p className="text-xl font-black tabular-nums tracking-wide" data-testid="offering-bank-account">{cfg.bank_account}</p>
                  <button type="button" onClick={() => { navigator.clipboard?.writeText(cfg.bank_account || ""); toast("Nomor rekening disalin"); }} data-testid="offering-copy-account" className="w-9 h-9 rounded-full bg-white/10 grid place-items-center"><Copy className="w-4 h-4" /></button></div>
                <p className="text-xs text-white/60">a.n. {cfg.bank_holder}</p>
              </div>
              {cfg.qris_image_url && <img src={fileUrl(cfg.qris_image_url)} alt="QRIS" className="w-48 mx-auto rounded-2xl border border-slate-100" data-testid="offering-qris-image" />}
              <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="offering-proof-input" />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="offering-proof-button" className="w-full border-2 border-dashed border-slate-200 rounded-2xl p-5 flex items-center gap-4 hover:border-blue-500 hover:bg-blue-50/40 transition">
                {proof?.preview && !proof.name.endsWith(".pdf") ? <img src={proof.preview} alt="" className="w-14 h-14 rounded-xl object-cover" /> : <span className="w-14 h-14 rounded-xl bg-blue-100 text-blue-700 grid place-items-center"><ImageUp className="w-6 h-6" /></span>}
                <span className="text-left"><span className="block font-bold text-slate-900 text-sm">{proof ? "Bukti terunggah" : "Unggah bukti transfer"}</span><span className="block text-xs text-slate-500 truncate max-w-[12rem]">{proof ? proof.name : "JPG, PNG, atau PDF · maks 8MB"}</span></span>
              </button>
            </div>
          )}
          <textarea data-testid="offering-note-input" rows={2} className={inputCls} placeholder="Catatan (opsional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <button onClick={submit} disabled={busy} data-testid="offering-submit-button" className={`${btnPrimary} w-full`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : form.method !== "manual" ? `Bayar via ${form.method === "xendit" ? "Xendit" : "Midtrans"}` : "Kirim Persembahan"}</button>
        </div>
        {history ? <History items={history} onRefresh={refresh} /> : <Skeleton className="h-24" />}
      </div>
    </div>
  );
}
